import React, { useCallback, useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { PapiDeviation } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import {
  AdoptAdoptablesClickBiEvent,
  BiData,
  MakeOfferAdoptablesClickBiEvent,
  ManageOfferAdoptablesClickBiEvent,
  ViewOffersAdoptablesClickBiEvent,
} from '@wix/da-bi/pkg/events';
import BuyButton from '@wix/da-ds/pkg/Buttons/BuyButton';
import { pushAdoptsBuyerModal } from '../../../Modals/redux/actionCreators';
import { useRestrictToLoggedInAndVerified } from '../../../utils/hooks/useRestrictToLoggedInAndVerified';
import ActionBarItemWrapper from '../../_partials/items/ActionBarItemWrapper';
import { getHasBuyItNow, getPrice } from '../../../utils/adoptables';
import { StandardDeviationElementPlacementContext } from '../../ElementPlacementContext/variants/standard/context';
import PriceWithDiscount from '../../../PriceWithDiscount';
import { getDeviationSaleDetails } from '../../../utils/deviations';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import type { AdoptableBuyerElementGroup } from '../../ElementPlacementContext/variants/standard/types';

import s from './AdoptableBuyer.scss';

export interface Props {
  deviation: PapiDeviation;
  /**
   * A stop gap to allow our stash actionbar to pass an element group without relying on the {@link StandardDeviationElementPlacementContext}.
   *
   * Passing this will override any context values, present or not.
   */
  // TODO: Figure out how to provide alternate element placement contexts based on bar type (stash, regular, simple, etc)
  manualElementGroup?: Omit<AdoptableBuyerElementGroup, 'groupPlacement' | 'countOnBar'> | false;
  className?: string;
}

export const AdoptableBuyer: React.FC<React.PropsWithChildren<Props>> = ({
  deviation,
  manualElementGroup,
  className,
}) => {
  const {
    elementGroups: { adoptableBuyer: contextElementGroup },
  } = useContext(StandardDeviationElementPlacementContext);
  const elementGroup = manualElementGroup ?? contextElementGroup;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { restrictToLoggedInAndVerified } = useRestrictToLoggedInAndVerified();
  const isMobile = useContext(MobileContext);
  const { eligibleDiscount, hasDiscounts, dollarAmount } = getDeviationSaleDetails(deviation, {
    optionalDecimalDigits: true,
  });
  const showDiscount = hasDiscounts;

  const openManageOfferModal = useCallback(() => {
    dispatch(pushAdoptsBuyerModal(deviation, 'offer'));
  }, [deviation, dispatch]);

  const restrictedOpenManageOfferModal = restrictToLoggedInAndVerified(
    ReferralType.ACTION_BAR,
    undefined,
    openManageOfferModal
  );

  const openViewOffersModal = useCallback(() => {
    dispatch(pushAdoptsBuyerModal(deviation, 'view-offers'));
  }, [deviation, dispatch]);

  const gotoCheckout = useCallback(() => {
    window.location.assign(
      Url.checkoutLink(deviation.extended?.adoptable?.fixedPrice?.subproductId ?? 0)
    );
  }, [deviation]);
  const restrictedGoToCheckout = restrictToLoggedInAndVerified(
    ReferralType.ACTION_BAR,
    undefined,
    gotoCheckout
  );

  const hasBuyItNow = getHasBuyItNow(deviation);
  const price = getPrice(deviation);

  if (isMobile || !elementGroup) {
    return null;
  }

  return (
    <ActionBarItemWrapper className={className} popoverWrapperClassName={s['wrapper']}>
      <div className={s['buttons']}>
        {elementGroup?.makeOffer && (
          <BuyButton
            variant={hasBuyItNow ? 'outline' : 'solid'}
            size={'medium'}
            rounded={true}
            biData={BiData<MakeOfferAdoptablesClickBiEvent>({
              evid: 751,
              itemid: deviation.deviationId,
              typeid: deviation.typeId,
              sectionname: 'action_bar',
            })}
            onClick={restrictedOpenManageOfferModal}
            className={s['button']}
          >
            {t('actionbar.adoptables.makeOffer')}
          </BuyButton>
        )}
        {elementGroup?.manageOffer && (
          <BuyButton
            variant={hasBuyItNow ? 'outline' : 'solid'}
            size={'medium'}
            rounded={true}
            biData={BiData<ManageOfferAdoptablesClickBiEvent>({
              evid: 753,
              itemid: deviation.deviationId,
              typeid: deviation.typeId,
              sectionname: 'action_bar',
            })}
            onClick={openManageOfferModal}
            className={s['button']}
          >
            {t('actionbar.adoptables.manageOffer')}
          </BuyButton>
        )}
        {elementGroup?.viewOffers && (
          <BuyButton
            variant={hasBuyItNow ? 'outline' : 'solid'}
            size={'medium'}
            rounded={true}
            onClick={openViewOffersModal}
            className={s['button']}
            biData={BiData<ViewOffersAdoptablesClickBiEvent>({
              evid: 746,
              itemid: deviation.deviationId,
              typeid: deviation.typeId,
              sectionname: 'action_bar',
            })}
          >
            {t('actionbar.adoptables.viewOffers')}
          </BuyButton>
        )}
        {elementGroup?.buyItNow && (
          <BuyButton
            variant={'solid'}
            size={'medium'}
            rounded={true}
            biData={BiData<AdoptAdoptablesClickBiEvent>({
              evid: 750,
              itemid: deviation.deviationId,
              typeid: deviation.typeId,
              purchase_price: parseFloat(price),
              sectionname: 'action_bar',
              is_gift: 0,
            })}
            onClick={restrictedGoToCheckout}
            className={s['button']}
          >
            {showDiscount ? (
              <PriceWithDiscount
                dollarAmount={dollarAmount ?? 0}
                eligibleDiscount={eligibleDiscount}
                size="medium"
                prefix={t('sidebar.exclusives.buttons.buyer.buyFor')}
                reversedTheme={true}
                opaque={false}
              />
            ) : (
              <Trans i18nKey={'actionbar.adoptables.adoptForDollars'}>Adopt for ${{ price }}</Trans>
            )}
          </BuyButton>
        )}
      </div>
    </ActionBarItemWrapper>
  );
};
AdoptableBuyer.displayName = 'AdoptableBuyerBranched';

export default AdoptableBuyer;
